import { getBrowserClient } from '@/lib/integrations/supabase/client';
import type { Business, BusinessHours, Preferences, BusinessUpdate, StaffRole } from '@/lib/integrations/supabase/types';

export async function getBusiness(businessId: string): Promise<Business | null> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('businesses')
    .select('*')
    .eq('id', businessId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data;
}

export async function getBusinessHours(businessId: string): Promise<BusinessHours[]> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('business_hours')
    .select('*')
    .eq('business_id', businessId)
    .order('day_of_week', { ascending: true });

  if (error) {
    throw error;
  }

  return data || [];
}

export async function getPreferences(businessId: string): Promise<Preferences | null> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('preferences')
    .select('*')
    .eq('business_id', businessId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data;
}

export interface CreateBusinessData {
  name: string;
  phone?: string;
  address?: string;
}

export async function createBusiness(data: CreateBusinessData): Promise<Business> {
  const supabase = getBrowserClient();
  const { data: { session } } = await supabase.auth.getSession();

  const headers: HeadersInit = { 'Content-Type': 'application/json' };

  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }

  const response = await fetch('/api/business/create', {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create business');
  }

  return response.json();
}

export async function updateBusiness(
  businessId: string,
  data: BusinessUpdate
): Promise<Business> {
  const supabase = getBrowserClient();
  const { data: { session } } = await supabase.auth.getSession();

  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }

  const response = await fetch(`/api/business/${businessId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update business');
  }

  return response.json();
}

export async function updateBusinessHours(
  businessId: string,
  hours: Partial<BusinessHours>[]
): Promise<void> {
  const supabase = getBrowserClient();
  const { data: { session } } = await supabase.auth.getSession();

  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }

  const response = await fetch(`/api/business/${businessId}/hours`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ hours }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update business hours');
  }
}

export async function updatePreferences(
  businessId: string,
  preferences: Partial<Preferences>
): Promise<void> {
  const supabase = getBrowserClient();
  const { data: { session } } = await supabase.auth.getSession();

  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }

  const response = await fetch(`/api/business/${businessId}/preferences`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(preferences),
  });

  if (!response.ok) {
    const error = await response.json();
    const errorMessage = error.message || error.error || 'Failed to update preferences';
    throw new Error(errorMessage);
  }
}

export async function updateServices(
  businessId: string,
  services: Array<{
    id?: string;
    name: string;
    name_ar?: string;
    duration_minutes: number;
    default_price_aed: number;
    active?: boolean;
  }>
): Promise<void> {
  const supabase = getBrowserClient();
  const { data: { session } } = await supabase.auth.getSession();

  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }

  const response = await fetch(`/api/business/${businessId}/services`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ services }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update services');
  }
}

export async function updateStaffRole(
  businessId: string,
  staffId: string,
  role: StaffRole
): Promise<void> {
  const supabase = getBrowserClient();
  const { data: { session } } = await supabase.auth.getSession();

  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }

  const response = await fetch(`/api/business/${businessId}/staff/${staffId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ role }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || error.message || 'Failed to update staff role');
  }
}

export async function updateProfile(data: {
  full_name?: string;
  email?: string;
  phone?: string;
}): Promise<void> {
  const supabase = getBrowserClient();
  const { data: { session } } = await supabase.auth.getSession();

  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }

  const response = await fetch('/api/profile', {
    method: 'PATCH',
    headers,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update profile');
  }
}
